import React, { useEffect, useState, useRef } from "react";
import studentImg from "../../assets/teacherImg.png";

import "../../styles/popup.css";
import { Toaster } from "react-hot-toast";
import Loader from "../Loader";
import UploadIcon from "../../assets/uploadImage.svg";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import Sidebar from "../DynamicComponents/SideBar.jsx";
import { useThemeContext } from "../../context/ThemeContext.jsx";
import dtuImg from "../../assets/dtufullimage.jpg";
import AlumniHeader from "./AlumniHeader.jsx";
import API from "../../utils/API.js";

import AlumniEducationDetails from "./Alumni Details/Tables/EducationDetails.jsx";
import AlumniPersonalDetails from "./Alumni Details/Tables/PersonalDetails.jsx";
import CurrentLocation from "./Alumni Details/Tables/CurrentLocation.jsx";
import AlumniAchievements from "./Alumni Details/Tables/Achievements.jsx";
import Experience from "./Alumni Details/Tables/Experience.jsx";

const Alumni = () => {
  const [isBlurActive, setBlurActive] = useState(false);
  const [loader, setLoader] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isOperationInProgress, setOperationInProgress] = useState(false);
  const [selectedSection, setSelectedSection] = useState("personal-info");
  const { darkMode } = useThemeContext();
  const { user_id, department_id, department_name, batch, alumni_name } =
    useSelector((state) => state.auth.user) || {};

  // Create refs for all sections
  const sectionRefs = {
    "personal-info": useRef(null),
    "current-location": useRef(null),
    "education-details": useRef(null),
    "professional-experience": useRef(null),
    "achievement-details": useRef(null),
  };

  const sidebarItems = [
    { id: "personal-info", label: "Personal Information" },
    { id: "current-location", label: "Current Location" },
    { id: "education-details", label: "Education" },
    { id: "professional-experience", label: "Professional Experience" },
    { id: "achievement-details", label: "Achievements" },
  ];

  const handleSidebarSelect = (sectionId) => {
    setSelectedSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      let currentSection = "personal-info";

      Object.keys(sectionRefs).forEach((key) => {
        const section = sectionRefs[key]?.current;
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            currentSection = key;
          }
        }
      });

      setSelectedSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchAlumniImage = async () => {
    if (isOperationInProgress) return;

    try {
      const response = await API.get(`${department_id}/alumni/profile-photo`, {
        params: { user_id },
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.document) {
        const imagePath = `${process.env.REACT_APP_BACKEND_URL}/${response.data.document}`;
        console.log("Fetched Image Path:", imagePath);
        setSelectedImage(imagePath);
      } else {
        setSelectedImage(studentImg);
      }
    } catch (error) {
      if (error.response && error.response.status >= 500) {
        toast.error("⚠️ Failed to fetch alumni image due to a server error.");
      } else if (error.response && error.response.status !== 404) {
        console.error("❌ Error fetching alumni image:", error);
      }
      setSelectedImage(studentImg);
    }
  };

  const handleImageUploadOrUpdate = async (event) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    setOperationInProgress(true);
    const file = event.target.files[0];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const allowedExtensions = ["jpg", "jpeg"];

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Only JPG or JPEG images are allowed.");
      setOperationInProgress(false);
      return;
    }

    const formData = new FormData();
    formData.append("document", file);

    try {
      const shouldUpdate = selectedImage && selectedImage !== studentImg;

      let response;
      if (shouldUpdate) {
        response = await API.put(
          `${department_id}/alumni/profile-photo?user_id=${user_id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        response = await API.post(
          `${department_id}/alumni/profile-photo?user_id=${user_id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (response.data?.document) {
        const imagePath = `${process.env.REACT_APP_BACKEND_URL}${response.data.document}`;
        fetchAlumniImage();
        toast.success("Image uploaded successfully!");
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImage(e.target.result);
          toast.success("Image uploaded successfully!");
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to upload image";
      toast.error(errorMessage);
      console.error("Image upload error:", error);
    } finally {
      setOperationInProgress(false);
    }
  };

  useEffect(() => {
    fetchAlumniImage();
    setTimeout(() => setLoader(false), 1000);
  }, []);

  return (
    <div
      className="pb-4"
      style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F4F5F7" }}
    >
      {loader ? (
        <Loader />
      ) : (
        <>
          <AlumniHeader />
          <div className="flex mt-4 min-h-screen">
            {/* Sidebar */}
            <div className="flex-shrink-0 sticky top-0 h-screen">
              <Sidebar
                menuItems={sidebarItems}
                selectedItem={selectedSection}
                onSelect={handleSidebarSelect}
                role="alumni"
                alumni_id={user_id}
              />
            </div>

            {/* Main Content */}
            <div
              className="flex-1 overflow-y-auto px-4 mb-10 pb-10"
              style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F4F5F7" }}
            >
              <div
                style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F4F5F7" }}
              >
                <div
                  className="rounded-2xl shadow-xl w-full"
                  style={{
                    backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
                    padding: "1px",
                    border: darkMode
                      ? "1px solid #22232B"
                      : "1px solid #D1D5DB",
                    boxShadow: darkMode
                      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                      : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {/* Hero Section with Profile */}
                  <div className="relative w-full shadow-md">
                    {/* Background Cover Image */}
                    <div
                      className="relative w-full h-[25vh] md:h-[30vh] lg:h-[37vh] rounded-t-2xl"
                      style={{
                        backgroundImage: `url(${dtuImg})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center 60%",
                        backgroundColor: darkMode ? "#2B2C3A" : "#E0E0E0",
                      }}
                    >
                      {/* Profile Image */}
                      <div className="absolute bottom-0 left-4 md:left-8 transform translate-y-1/3">
                        <img
                          className="rounded-full h-24 w-24 md:h-36 md:w-36 border-4 border-white dark:border-gray-800 object-cover"
                          src={selectedImage ? selectedImage : studentImg}
                          alt="Alumni Profile"
                        />
                        {/* Upload Image Icon */}
                        <label
                          htmlFor="imageUpload"
                          className={`absolute bottom-0 right-0 p-1 rounded-full cursor-pointer transition-all ${
                            darkMode
                              ? "bg-gray-600 hover:bg-gray-700"
                              : "bg-gray-300 hover:bg-gray-400"
                          }`}
                        >
                          <img
                            src={UploadIcon}
                            alt="Upload"
                            className="w-5 h-5"
                          />
                          <input
                            type="file"
                            id="imageUpload"
                            className="hidden"
                            accept=".jpg,.jpeg"
                            onChange={handleImageUploadOrUpdate}
                            disabled={isOperationInProgress}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Grid Layout for Alumni Details */}
                  <div className="grid grid-cols-1 gap-6 mt-16 px-4 md:px-6 mb-6 pb-4">
                    {/* Alumni Details */}
                    <div
                      className="rounded-xl p-5 md:p-6 text-center md:text-left flex flex-col"
                      style={{
                        backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
                        border: darkMode
                          ? "2px solid #22232B"
                          : "2px solid #D1D5DB",
                      }}
                    >
                      {/* Alumni Name */}
                      <h1
                        className="text-xl md:text-3xl font-semibold"
                        style={{ color: darkMode ? "#EAEAEA" : "#1F252E" }}
                      >
                        {alumni_name}
                      </h1>

                      {/* Divider */}
                      <div
                        className="w-full"
                        style={{
                          height: "1.2px",
                          backgroundColor: darkMode ? "#2B2C3A" : "#D1D5DB",
                          marginTop: "3px",
                        }}
                      />

                      {/* Alumni Status and Batch */}
                      <h2
                        className="text-sm md:text-base italic font-medium mt-1"
                        style={{ color: darkMode ? "#B0B3B8" : "#4A5568" }}
                      >
                        Batch of {batch}
                      </h2>

                      {/* Additional Details */}
                      <div className="flex flex-col gap-1 text-sm md:text-base font-medium mt-2">
                        <div className="flex justify-between md:justify-start gap-2">
                          <span
                            style={{ color: darkMode ? "#B0B3B8" : "#6B7280" }}
                          >
                            User ID:
                          </span>
                          <span
                            style={{ color: darkMode ? "#EAEAEA" : "#1F252E" }}
                          >
                            {user_id}
                          </span>
                        </div>
                        <div className="flex justify-between md:justify-start gap-2">
                          <span
                            style={{ color: darkMode ? "#B0B3B8" : "#6B7280" }}
                          >
                            Department:
                          </span>
                          <span
                            style={{ color: darkMode ? "#EAEAEA" : "#1F252E" }}
                          >
                            {department_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Sections with refs */}
                <div
                  style={{ color: darkMode ? "#F8F9FA" : "#1F252E" }}
                  ref={sectionRefs["personal-info"]}
                  className={`pt-6 pb-3 ${isBlurActive ? "blur-effect" : ""}`}
                >
                  <AlumniPersonalDetails setBlurActive={setBlurActive} />
                </div>

                <div
                  style={{ color: darkMode ? "#F8F9FA" : "#1F252E" }}
                  ref={sectionRefs["current-location"]}
                  className={`pt-6 pb-3 ${isBlurActive ? "blur-effect" : ""}`}
                >
                  <CurrentLocation setBlurActive={setBlurActive} />
                </div>
                <div
                  style={{ color: darkMode ? "#F8F9FA" : "#1F252E" }}
                  ref={sectionRefs["education-details"]}
                  className={`pt-6 pb-3 ${isBlurActive ? "blur-effect" : ""}`}
                >
                  <AlumniEducationDetails setBlurActive={setBlurActive} />
                </div>
                <div
                  style={{ color: darkMode ? "#F8F9FA" : "#1F252E" }}
                  ref={sectionRefs["professional-experience"]}
                  className={`pt-6 pb-3 ${isBlurActive ? "blur-effect" : ""}`}
                >
                  <Experience setBlurActive={setBlurActive} />
                </div>
                <div
                  style={{ color: darkMode ? "#F8F9FA" : "#1F252E" }}
                  ref={sectionRefs["achievement-details"]}
                  className={`pt-6 pb-3 ${isBlurActive ? "blur-effect" : ""}`}
                >
                  <AlumniAchievements setBlurActive={setBlurActive} />
                </div>

                <Toaster
                  toastOptions={{
                    duration: 1000,
                    style: {
                      background: darkMode ? "#161722" : "#FFFFFF",
                      color: darkMode ? "#F8F9FA" : "#1F252E",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Alumni;
